/**
 * API Error Handler Utility
 *
 * Provides centralized error handling for API responses
 * with consistent error message extraction and formatting
 */

export interface ApiError {
  status?: number
  message?: string
  details?: any
  body?: any
  response?: {
    data?: any
    status?: number
  }
}

/**
 * Extract error message from various API error response formats
 */
export const extractErrorMessage = (error: any): string => {
  // Handle null or undefined errors
  if (!error) {
    return '알 수 없는 오류가 발생했습니다.'
  }

  // Extract error data from different response structures
  const errorData = error?.body || error?.response?.data || error
  const errorMessages: string[] = []

  // Collect all available error information
  if (errorData?.details) {
    if (Array.isArray(errorData.details)) {
      errorMessages.push(...errorData.details.map((d: any) =>
        typeof d === 'string' ? d : JSON.stringify(d)
      ))
    } else if (typeof errorData.details === 'object') {
      errorMessages.push(JSON.stringify(errorData.details))
    } else {
      errorMessages.push(String(errorData.details))
    }
  }

  // Add main error message if available
  if (errorData?.message) {
    errorMessages.push(errorData.message)
  }

  if (errorData?.error) {
    errorMessages.push(errorData.error)
  }

  // If we found specific error messages, return them
  if (errorMessages.length > 0) {
    return errorMessages.join(': ')
  }

  // Otherwise, return status code based message
  return getErrorMessageByStatus(error?.status || error?.response?.status)
}

/**
 * Get localized error message based on HTTP status code
 */
export const getErrorMessageByStatus = (status?: number): string => {
  switch (status) {
    case 400:
      return '요청이 올바르지 않습니다. 입력 내용을 확인해주세요.'
    case 401:
      return '인증이 만료되었습니다. 다시 로그인해주세요.'
    case 403:
      return '권한이 없습니다. 접근 권한을 확인해주세요.'
    case 404:
      return '요청한 항목을 찾을 수 없습니다.'
    case 409:
      return '이미 존재하는 항목입니다.'
    case 422:
      return '입력한 데이터가 유효하지 않습니다.'
    case 429:
      return '너무 많은 요청을 보냈습니다. 잠시 후 다시 시도해주세요.'
    case 500:
      return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
    case 502:
    case 503:
    case 504:
      return '서버가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.'
    default:
      return '오류가 발생했습니다. 다시 시도해주세요.'
  }
}

// Cache for preventing duplicate error messages
const errorCache = new Map<string, number>()
const ERROR_CACHE_TIMEOUT = 2000 // 2 seconds

/**
 * Handle API error with Alert component
 * @param error - The error object from API call
 * @param showError - The showError function from useAlert hook
 * @param customMessage - Optional custom error message prefix
 */
export const handleApiError = (
  error: any,
  showError: (message: string) => void,
  customMessage?: string
): void => {
  const errorMessage = extractErrorMessage(error)
  const finalMessage = customMessage
    ? `${customMessage}: ${errorMessage}`
    : errorMessage

  // Create a cache key for this error
  const cacheKey = `${customMessage || 'generic'}_${errorMessage}`
  const now = Date.now()

  // Check if we've shown this error recently
  const lastShown = errorCache.get(cacheKey)
  if (lastShown && (now - lastShown) < ERROR_CACHE_TIMEOUT) {
    console.log('Duplicate error message prevented:', finalMessage)
    return
  }

  // Cache this error
  errorCache.set(cacheKey, now)

  // Clean up old cache entries
  for (const [key, timestamp] of errorCache.entries()) {
    if (now - timestamp > ERROR_CACHE_TIMEOUT) {
      errorCache.delete(key)
    }
  }

  showError(finalMessage)

  // Log error for debugging
  console.error('API Error:', {
    error,
    extractedMessage: errorMessage,
    finalMessage
  })
}

/**
 * Create context-specific error messages
 */
export const ErrorMessages = {
  // Authentication
  LOGIN_FAILED: '로그인에 실패했습니다',
  LOGOUT_FAILED: '로그아웃에 실패했습니다',

  // CRUD Operations
  CREATE_FAILED: '생성에 실패했습니다',
  UPDATE_FAILED: '수정에 실패했습니다',
  DELETE_FAILED: '삭제에 실패했습니다',
  FETCH_FAILED: '데이터를 불러오는데 실패했습니다',

  // Specific entities
  BUSINESS_CREATE_FAILED: '사업 생성에 실패했습니다',
  BUSINESS_UPDATE_FAILED: '사업 수정에 실패했습니다',
  BUSINESS_DELETE_FAILED: '사업 삭제에 실패했습니다',

  TASK_GROUP_CREATE_FAILED: '전략과제 생성에 실패했습니다',
  TASK_GROUP_UPDATE_FAILED: '전략과제 수정에 실패했습니다',
  TASK_GROUP_DELETE_FAILED: '전략과제 삭제에 실패했습니다',

  TASK_CREATE_FAILED: '과제 생성에 실패했습니다',
  TASK_UPDATE_FAILED: '과제 수정에 실패했습니다',
  TASK_DELETE_FAILED: '과제 삭제에 실패했습니다',

  APPROVAL_FAILED: '승인에 실패했습니다',
  ASSIGNEE_APPROVAL_FAILED: '담당자 승인에 실패했습니다',
  MANAGER_APPROVAL_FAILED: '관리자 승인에 실패했습니다',
  REVIEWER_APPROVAL_FAILED: '검토자 승인에 실패했습니다',

  // Validation
  VALIDATION_FAILED: '입력 검증에 실패했습니다',
  REQUIRED_FIELD_MISSING: '필수 항목을 입력해주세요',
  WORKSPACE_NOT_SELECTED: '워크스페이스가 선택되지 않았습니다',
  WORKSPACE_INFO_MISSING: '워크스페이스 정보가 없습니다',

  // Network
  NETWORK_ERROR: '네트워크 연결을 확인해주세요',
  TIMEOUT_ERROR: '요청 시간이 초과되었습니다',
} as const

/**
 * Type-safe error message helper
 */
export const getErrorMessage = (
  key: keyof typeof ErrorMessages
): string => {
  return ErrorMessages[key]
}